import { DEFAULT_LANGUAGE } from '@/constants';
import { type IHeader, LanguageCode } from '@/interfaces';
import {
  getMasterTranslationLanguageCode,
  headerDataModify,
  languageFieldsMapping,
  translationDataModify,
} from '@/utils';
import callContentful from './base.fetchers';

type GetHeader = (args: {
  isPreviewMode: boolean;
  languageCode: LanguageCode;
  slug: string;
}) => Promise<IHeader>;

const getHeader: GetHeader = async ({ isPreviewMode, languageCode = LanguageCode.English, slug }) => {
  const mappedLanguageFields = languageFieldsMapping(languageCode);
  const languageField = getMasterTranslationLanguageCode(languageCode);

  const query = `
    {
      defaultHeaderCollection: headerCollection(
        preview: ${isPreviewMode}
        locale: "${DEFAULT_LANGUAGE}"
        limit: 1
      ) {
        items {
          itemList
          mainText {
            en
          }
          subText {
            en
          }
          button {
            en
          }
          reportsPopup
        }
      }
      headerCollection(
        preview: ${isPreviewMode},
        locale: "${mappedLanguageFields.locale}"
        limit: 1,
      ) {
        items {
          itemList
          mainText {
            ${languageField}
          }
          subText {
            ${languageField}
          }
          button {
            ${languageField}
          }
          reportsPopup
        }
      }
    }
    `;

  const response = await callContentful(query, isPreviewMode, slug, 'header.fetchers.getHeader');
  const rawDefaultHeaderData = response?.data?.defaultHeaderCollection?.items?.filter(Boolean)[0] || null;
  const rawHeaderData = response?.data?.headerCollection?.items?.filter(Boolean)[0] || null;

  // Transform the linked multiLanguageField data to flat string values
  let transformedDefaultHeaderData = rawDefaultHeaderData;
  if (rawDefaultHeaderData) {
    const defaultMainText = rawDefaultHeaderData.mainText as Record<string, string> | null;
    const defaultSubText = rawDefaultHeaderData.subText as Record<string, string> | null;
    const defaultButton = rawDefaultHeaderData.button as Record<string, string> | null;

    transformedDefaultHeaderData = {
      ...rawDefaultHeaderData,
      mainText: defaultMainText?.en || '',
      subText: defaultSubText?.en || '',
      button: defaultButton?.en || '',
    };
  }

  let transformedHeaderData = rawHeaderData;
  if (rawHeaderData) {
    const mainText = rawHeaderData.mainText as Record<string, string> | null;
    const subText = rawHeaderData.subText as Record<string, string> | null;
    const button = rawHeaderData.button as Record<string, string> | null;

    transformedHeaderData = {
      ...rawHeaderData,
      mainText: mainText?.[languageField] || '',
      subText: subText?.[languageField] || '',
      button: button?.[languageField] || '',
    };
  }

  const data = translationDataModify(transformedDefaultHeaderData, transformedHeaderData);
  return headerDataModify(data);
};

export { getHeader };
